/**
 * How an output table's rows are written. The source table can take the
 * result in the row a run is processing (`source`, the table's
 * `update_source` mode). Otherwise the mode follows from the table's match
 * config: none → a new row per run (`append`); match keys that fail without
 * a hit → the existing row is updated (`update`); match keys that create
 * without a hit → updated or created (`upsert`).
 *
 * A match key names a target column and the response field that is compared
 * with it; only columns with a mapped response field can be keys.
 *
 * Pure: every function returns the next match configs and output tables.
 */
import type {
  MatchConfigProps,
  MultiMatchBehavior,
  OutputMapping,
  OutputTable,
  TargetMatchMode,
} from '../../services/automation';

export type RowWriteMode = 'source' | 'append' | 'update' | 'upsert';

export interface RowWriteState {
  matchConfigs: MatchConfigProps[];
  outputTables: OutputTable[];
}

/** A target column that can identify the row: it has a mapped response field. */
export interface RowKeyCandidate {
  columnId: string;
  responseField: string;
}

export function createMatchConfigId(): string {
  return `mc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function rowMatchConfigOf(
  state: RowWriteState,
  tableId: string
): MatchConfigProps | undefined {
  const matchConfigId = state.outputTables.find(
    table => table.tableId === tableId
  )?.matchConfigId;
  return matchConfigId
    ? state.matchConfigs.find(config => config.id === matchConfigId)
    : undefined;
}

/**
 * `updatesSourceRow`: the table is the source table and set to update the
 * processed row (the automation contract's `updatesSourceRow`).
 */
export function rowWriteModeOf(
  matchConfig: MatchConfigProps | undefined,
  updatesSourceRow = false
): RowWriteMode {
  if (updatesSourceRow) return 'source';
  if (!matchConfig || matchConfig.keys.length === 0) return 'append';
  return matchConfig.notFoundBehavior === 'fail' ? 'update' : 'upsert';
}

export function rowKeyCandidates(
  tableId: string,
  mappings: readonly OutputMapping[]
): RowKeyCandidate[] {
  return mappings
    .filter(mapping => mapping.tableId === tableId && mapping.responseField)
    .map(mapping => ({
      columnId: mapping.targetColumnId,
      responseField: mapping.responseField,
    }));
}

function replaceConfig(
  state: RowWriteState,
  next: MatchConfigProps
): RowWriteState {
  return {
    ...state,
    matchConfigs: state.matchConfigs.map(config =>
      config.id === next.id ? next : config
    ),
  };
}

function removeConfig(
  state: RowWriteState,
  tableId: string,
  matchConfig: MatchConfigProps
): RowWriteState {
  return {
    matchConfigs: state.matchConfigs.filter(
      config => config.id !== matchConfig.id
    ),
    outputTables: state.outputTables.map(table =>
      table.tableId === tableId ? { ...table, matchConfigId: undefined } : table
    ),
  };
}

/**
 * The table writes into the processed row: no row matching and not one row
 * per list entry.
 */
function writeToSourceRow(
  state: RowWriteState,
  tableId: string
): RowWriteState {
  const current = rowMatchConfigOf(state, tableId);
  const { matchConfigs } = current
    ? removeConfig(state, tableId, current)
    : state;
  return {
    matchConfigs,
    outputTables: state.outputTables.map(table => {
      if (table.tableId !== tableId) return table;
      const {
        matchConfigId: _matchConfigId,
        sourceArrayField: _sourceArrayField,
        ...rest
      } = table;
      return { ...rest, mode: 'update_source' };
    }),
  };
}

/** The table no longer writes into the processed row. */
function leaveSourceRow(state: RowWriteState, tableId: string): RowWriteState {
  if (
    !state.outputTables.some(
      table => table.tableId === tableId && table.mode === 'update_source'
    )
  ) {
    return state;
  }
  return {
    ...state,
    outputTables: state.outputTables.map(table => {
      if (table.tableId !== tableId) return table;
      const { mode: _mode, ...rest } = table;
      return rest;
    }),
  };
}

/**
 * Sets the write mode. Updating needs a key: without one yet, the first
 * candidate becomes the key (none → the state stays as it is).
 */
export function setRowWriteMode(
  previous: RowWriteState,
  tableId: string,
  mode: RowWriteMode,
  candidates: readonly RowKeyCandidate[],
  createId: () => string = createMatchConfigId
): RowWriteState {
  if (mode === 'source') return writeToSourceRow(previous, tableId);
  const current = rowMatchConfigOf(previous, tableId);
  const hasKeys = !!current && current.keys.length > 0;
  if (mode !== 'append' && !hasKeys && candidates.length === 0) return previous;
  const state = leaveSourceRow(previous, tableId);
  if (mode === 'append') {
    return current ? removeConfig(state, tableId, current) : state;
  }
  const notFoundBehavior = mode === 'update' ? 'fail' : 'create';
  if (current && current.keys.length > 0) {
    return replaceConfig(state, { ...current, notFoundBehavior });
  }
  const first = candidates[0];
  if (!first) return previous;
  const keys = [
    {
      columnId: first.columnId,
      sourceField: first.responseField,
      matchMode: 'auto',
    },
  ];
  if (current)
    return replaceConfig(state, { ...current, notFoundBehavior, keys });
  const id = createId();
  return {
    matchConfigs: [
      ...state.matchConfigs,
      {
        id,
        targetTableId: tableId,
        multiMatchBehavior: 'fail',
        notFoundBehavior,
        keys,
      },
    ],
    outputTables: state.outputTables.map(table =>
      table.tableId === tableId ? { ...table, matchConfigId: id } : table
    ),
  };
}

/** Puts `candidate` at `index` (replacing that key) or adds it as a new key. */
export function setRowKey(
  state: RowWriteState,
  tableId: string,
  index: number,
  candidate: RowKeyCandidate
): RowWriteState {
  const current = rowMatchConfigOf(state, tableId);
  if (!current) return state;
  if (
    current.keys.some(
      (key, position) =>
        position !== index && key.columnId === candidate.columnId
    )
  ) {
    return state;
  }
  const keys = [...current.keys];
  keys[index] = {
    columnId: candidate.columnId,
    sourceField: candidate.responseField,
    matchMode: keys[index]?.matchMode ?? 'auto',
  };
  return replaceConfig(state, { ...current, keys });
}

/** Removes a key; the last one stays (the write mode decides about matching). */
export function removeRowKey(
  state: RowWriteState,
  tableId: string,
  index: number
): RowWriteState {
  const current = rowMatchConfigOf(state, tableId);
  if (!current || current.keys.length <= 1) return state;
  return replaceConfig(state, {
    ...current,
    keys: current.keys.filter((_, position) => position !== index),
  });
}

export function setRowKeyMatchMode(
  state: RowWriteState,
  tableId: string,
  index: number,
  matchMode: TargetMatchMode
): RowWriteState {
  const current = rowMatchConfigOf(state, tableId);
  if (!current?.keys[index]) return state;
  return replaceConfig(state, {
    ...current,
    keys: current.keys.map((key, position) =>
      position === index ? { ...key, matchMode } : key
    ),
  });
}

export function setRowMultiMatch(
  state: RowWriteState,
  tableId: string,
  multiMatchBehavior: MultiMatchBehavior
): RowWriteState {
  const current = rowMatchConfigOf(state, tableId);
  if (!current) return state;
  return replaceConfig(state, { ...current, multiMatchBehavior });
}

/**
 * Keeps the keys in step with the mappings: a key compares the response
 * field mapped to its column, and a column without one is no key any more.
 * Without keys left the table appends again. Returns `state` itself when
 * nothing changes.
 */
export function syncRowKeys(
  state: RowWriteState,
  tableId: string,
  mappings: readonly OutputMapping[]
): RowWriteState {
  const current = rowMatchConfigOf(state, tableId);
  if (!current) return state;
  const fieldOf = new Map(
    rowKeyCandidates(tableId, mappings).map(candidate => [
      candidate.columnId,
      candidate.responseField,
    ])
  );
  const keys = current.keys.flatMap(key => {
    const sourceField = fieldOf.get(key.columnId);
    return sourceField ? [{ ...key, sourceField }] : [];
  });
  const unchanged =
    keys.length === current.keys.length &&
    keys.every(
      (key, index) => key.sourceField === current.keys[index].sourceField
    );
  if (unchanged) return state;
  return keys.length === 0
    ? removeConfig(state, tableId, current)
    : replaceConfig(state, { ...current, keys });
}
